/**
 * MembershipPerks — Phase 2b (v2)
 * "What You Get When You Join" — punchier cards, accurate copy that
 * reflects actual membership tiers (Fitness-only · Til-I-Die · Pro)
 * and the real self-renewal benefits (bonus days for all plans,
 * cash discounts for 6 & 12 month).
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import {
  Dumbbell, Trophy, Calendar, Sparkles, Users, RefreshCw, Smartphone, Snowflake, Gamepad2, Utensils, Sofa, Swords, CalendarDays,
} from 'lucide-react';

const PERKS = [
  {
    icon: Calendar,
    title: 'Classes for your tier',
    body: 'Fitness-only: S&C + Circuit. Til-I-Die: full combat — Muay Thai · Boxing · Kickboxing · BJJ · Wrestling · MMA · Sparring · Open Mat. Pro: everything + Pro Class.',
    badge: null,
    tone: 'red',
  },
  {
    icon: Dumbbell,
    title: 'Full gym access',
    body: 'Squat rack, dumbbells (up to 35kg), cables, V-squat, leg press, deadlift / farmer\'s-carry bar, ski-erg, rower, bike.',
    badge: null,
    tone: 'red',
  },
  {
    icon: Trophy,
    title: 'Game Plan — free training app',
    body: 'A personalised weekly plan + auto-tracked personal bests + start-to-end position photos on every lift. Tier-aware — fitness members get S&C + Circuit plans, combat members get fight-camp programming.',
    badge: 'FREE',
    tone: 'red',
    highlight: true,
  },
  {
    icon: RefreshCw,
    title: 'Self-renew & save real money',
    body: 'Every plan earns bonus days when you renew yourself (3 days on monthly, up to 15 on 12-month). 6 & 12-month members also unlock up to 20% cash discount on renewals. Build a streak for +5 more days forever.',
    badge: 'SAVE 20%',
    tone: 'green',
    highlight: true,
  },
  {
    icon: Users,
    title: 'Coaches who know your name',
    body: 'Real coaches, real corrections, real progress.',
    badge: null,
    tone: 'red',
  },
  {
    icon: Snowflake,
    title: 'Ice baths & recovery',
    body: 'Cold tubs, mats, foam-rollers and dedicated recovery space — because recovery isn\'t optional if you train hard. All included with your membership.',
    badge: null,
    tone: 'red',
  },
  {
    icon: Utensils,
    title: 'In-house Kitchen',
    body: 'Refuel at our in-house kitchen — proper meals, protein shakes, snacks and drinks made for people who train. Open to members and non-members alike.',
    badge: 'ALL WELCOME',
    tone: 'red',
  },
  {
    icon: Sofa,
    title: 'Chill-out lounge',
    body: 'Sofas, comfy chairs, a free Xbox & TV — somewhere to hang out, recover or wait in comfort. Free for everyone, members or not.',
    badge: 'ALL WELCOME',
    tone: 'red',
  },
  {
    icon: Gamepad2,
    title: 'Cobra Arcade',
    body: 'Members-only games right inside the app — including Cobra: The Comeback, our own side-scrolling platformer — plus Heavy Bag Blitz, Combo Cobra & Cobra Crawl, all with live weekly leaderboards. A bit of fun, and bragging rights over the rest of the gym.',
    badge: 'NEW',
    tone: 'purple',
    highlight: true,
  },
  {
    icon: Swords,
    title: 'Skills Tracker',
    body: 'Track your progress across every discipline — Muay Thai, Boxing, BJJ & Wrestling. See your rank, tick off techniques as you master them and watch your skill tree grow, right inside the app.',
    badge: 'NEW',
    tone: 'red',
    highlight: true,
  },
  {
    icon: CalendarDays,
    title: 'India Fight Calendar',
    body: 'Never miss a card — every upcoming MMA, BJJ, Muay Thai, boxing & wrestling event across India in one place, right in your app. Filter by discipline and get dates, venues and links.',
    badge: 'NEW',
    tone: 'red',
    highlight: true,
  },
  {
    icon: Smartphone,
    title: 'Your member app',
    body: 'Track payments, see your renewal window, log lifts, sign your waiver and play the arcade — all from your phone. Add it to your home screen and it works just like an app.',
    badge: null,
    tone: 'red',
  },
  {
    icon: Sparkles,
    title: 'Sparring nights & events',
    body: 'Optional open sparring on Friday and Saturday nights — only for those who want it, never compulsory. Plus in-house tournaments and seminars from our elite coaches and pro fighters, and a few times a year we fly in international fighters and coaches too.',
    badge: null,
    tone: 'red',
  },
];


export default function MembershipPerks({ compact = false }) {
  const [headRef, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  // Highlight styles per tone (purple = Cobra Arcade, matching the app's fuchsia accent)
  const HL = {
    red: {
      card: 'border-cobra-red/40 bg-gradient-to-br from-cobra-red/15 via-cobra-red/5 to-black hover:border-cobra-red/80 hover:shadow-[0_0_30px_rgba(164,0,3,0.25)]',
      blob: 'bg-cobra-red/30',
      iconBox: 'border-cobra-red/60 bg-cobra-red/20 group-hover:bg-cobra-red/30',
      icon: 'text-cobra-red',
      badge: 'text-cobra-red bg-cobra-red/15 border-cobra-red/40',
    },
    green: {
      card: 'border-emerald-500/40 bg-gradient-to-br from-emerald-900/25 via-emerald-950/20 to-black hover:border-emerald-400/70 hover:shadow-[0_0_30px_rgba(16,185,129,0.18)]',
      blob: 'bg-emerald-500/30',
      iconBox: 'border-emerald-400/50 bg-emerald-500/20 group-hover:bg-emerald-500/30',
      icon: 'text-emerald-300',
      badge: 'text-emerald-300 bg-emerald-500/15 border-emerald-400/40',
    },
    purple: {
      card: 'border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-900/30 via-fuchsia-950/20 to-black hover:border-fuchsia-400/70 hover:shadow-[0_0_30px_rgba(217,70,239,0.22)]',
      blob: 'bg-fuchsia-500/30',
      iconBox: 'border-fuchsia-400/50 bg-fuchsia-500/20 group-hover:bg-fuchsia-500/30',
      icon: 'text-fuchsia-300',
      badge: 'text-fuchsia-300 bg-fuchsia-500/15 border-fuchsia-400/40',
    },
  };
  const NON_HL = {
    card: 'border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent hover:border-cobra-red/40 hover:bg-white/[0.06]',
    iconBox: 'border-white/15 bg-white/5 group-hover:border-cobra-red/40 group-hover:bg-cobra-red/10',
    icon: 'text-white/85 group-hover:text-cobra-red transition-colors',
    badge: 'text-cobra-red bg-cobra-red/15 border-cobra-red/40',
  };

  return (
    <section
      className={`relative ${compact ? 'py-16' : 'py-24'} px-4 md:px-8 lg:px-12 overflow-hidden`}
      data-testid="membership-perks"
      style={{
        background: 'radial-gradient(ellipse at top, rgba(164,0,3,0.08) 0%, transparent 55%), #050505',
      }}
    >
      {/* Subtle grid overlay for texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        <motion.div
          ref={headRef}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <p className="text-cobra-red font-manrope text-xs md:text-sm tracking-[0.4em] uppercase font-bold mb-3">
            Why Cobra Thai
          </p>
          <h2 className="font-anton text-4xl md:text-5xl lg:text-6xl text-white uppercase tracking-wider leading-none">
            What you get <span className="text-cobra-red text-shadow-glow">when you join</span>
          </h2>
          <p className="font-manrope text-white/60 mt-5 text-base md:text-lg max-w-2xl mx-auto">
            One membership. Every tool, every class your tier covers, every coach in the building. Plus a free training app that holds you accountable.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {PERKS.map((perk, i) => {
            const Icon = perk.icon;
            const isHighlight = !!perk.highlight;
            const hl = HL[perk.tone] || HL.red;
            return (
              <motion.div
                key={perk.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                whileHover={{ y: -4 }}
                className={`group relative rounded-sm border p-6 transition-all duration-300 ${
                  isHighlight ? hl.card : NON_HL.card
                }`}
                data-testid={`perk-${perk.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}
              >
                {/* Glow blob behind icon */}
                {isHighlight && (
                  <div
                    aria-hidden
                    className={`absolute -top-8 -left-8 w-24 h-24 rounded-full blur-3xl ${hl.blob}`}
                  />
                )}

                <div className="relative">
                  <div
                    className={`w-12 h-12 rounded-sm border flex items-center justify-center mb-4 transition-colors ${
                      isHighlight ? hl.iconBox : NON_HL.iconBox
                    }`}
                  >
                    <Icon
                      size={22}
                      className={isHighlight ? hl.icon : NON_HL.icon}
                    />
                  </div>
                  <h3 className="font-anton text-xl md:text-[22px] text-white uppercase tracking-wider leading-tight mb-2.5">
                    {perk.title}
                  </h3>
                  <p className="font-manrope text-white/65 text-[13.5px] leading-relaxed">
                    {perk.body}
                  </p>
                </div>

                {perk.badge && (
                  <span
                    className={`absolute top-3 right-3 text-[10px] font-bold tracking-[0.2em] uppercase px-2 py-0.5 rounded-sm border ${
                      isHighlight ? hl.badge : NON_HL.badge
                    }`}
                  >
                    {perk.badge}
                  </span>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Non-member top-of-funnel hook — Kitchen & Lounge are open to all */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.6 }}
          data-testid="non-member-hook"
          className="relative mt-10 rounded-sm border border-cobra-red/30 bg-gradient-to-br from-cobra-red/10 via-cobra-red/[0.04] to-black p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 overflow-hidden"
        >
          <div
            aria-hidden
            className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl bg-cobra-red/20 pointer-events-none"
          />
          <div className="relative flex items-center gap-3 shrink-0">
            <div className="w-12 h-12 rounded-sm border border-cobra-red/40 bg-cobra-red/15 flex items-center justify-center">
              <Utensils size={22} className="text-cobra-red" />
            </div>
            <div className="w-12 h-12 rounded-sm border border-white/15 bg-white/5 flex items-center justify-center">
              <Sofa size={22} className="text-white/85" />
            </div>
          </div>
          <div className="relative flex-1">
            <h3 className="font-anton text-xl md:text-2xl text-white uppercase tracking-wider leading-tight">
              Not a member yet? You're still welcome.
            </h3>
            <p className="font-manrope text-white/65 text-sm md:text-[15px] mt-1.5 leading-relaxed">
              Walk in, grab a shake at our in-house kitchen, kick back in the lounge with the Xbox — and book a free trial while you're here.
            </p>
          </div>
          <Link
            to="/?scroll=form"
            data-testid="non-member-free-trial-cta"
            className="relative shrink-0 inline-flex items-center justify-center gap-2 bg-cobra-red text-white hover:bg-red-800 uppercase font-bold tracking-wider rounded-sm px-7 py-3.5 text-sm transition-all duration-300 hover:scale-105 skew-button"
          >
            <span>Get your free trial</span>
            <span aria-hidden>&rarr;</span>
          </Link>
        </motion.div>

      </div>
    </section>
  );
}
